import { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { authApi } from '../../api/auth.api';
import { getAccessToken } from '../../api/client';
import { useAuthStore } from '../../stores/auth.store';
import LoadingScreen from '../../components/ui/LoadingScreen';
import { Icon, Check, ArrowRight } from '../../components/ui/Icon';

type Status = 'verifying' | 'success' | 'error';

export default function VerifyEmailPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { user, setAuth } = useAuthStore();
  const { t } = useTranslation();
  const token = params.get('token') ?? '';

  const [status, setStatus] = useState<Status>('verifying');

  useEffect(() => {
    if (!token) { setStatus('error'); return; }
    authApi.verifyEmail(token)
      .then(async () => {
        const token = getAccessToken();
        if (user && token) {
          const me = await authApi.me();
          setAuth(me, token);
        }
        setStatus('success');
      })
      .catch(() => setStatus('error'));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (status === 'verifying') return <LoadingScreen fullscreen label={t('auth.verify.checking')} />;

  return (
    <div className="min-h-screen flex items-center justify-center" style={{ background: '#faf7f0' }}>
      <div className="max-w-md px-8 text-center">
        <div
          className={`w-16 h-16 mx-auto mb-6 rounded-2xl flex items-center justify-center ${
            status === 'success' ? 'bg-teal-100' : 'bg-slate-100'
          }`}
        >
          {status === 'success' ? (
            <Icon as={Check} size={32} className="text-teal-600" />
          ) : (
            <span className="text-3xl">✕</span>
          )}
        </div>

        <h1 className="font-serif text-4xl font-light leading-tight mb-3" style={{ color: '#0b1420', letterSpacing: -1 }}>
          {status === 'success' ? t('auth.verify.success') : t('auth.verify.failed')}
        </h1>
        <p className="text-sm leading-relaxed mb-7" style={{ color: '#3a4558' }}>
          {status === 'success'
            ? t('auth.verify.successBody')
            : t('auth.verify.failedBody')}
        </p>

        <button
          onClick={() => navigate(user ? '/dashboard' : '/login')}
          className="inline-flex items-center gap-3 font-serif font-medium text-base px-6 py-3 rounded-xl transition-colors"
          style={{ background: '#0b1420', color: '#faf7f0' }}
        >
          {user ? t('auth.verify.goToDashboard') : t('auth.verify.goHome')}
          <span className="w-6 h-6 rounded-full flex items-center justify-center not-italic" style={{ background: '#d9b382', color: '#0b1420' }}>
            <Icon as={ArrowRight} size={12} />
          </span>
        </button>
      </div>
    </div>
  );
}
